import contractRoutes from '@/modules/contract/routes';
import customerGroupRoutes from '@/modules/customer-groups/routes';
import customerRoutes from '@/modules/customer/routes';
import employerRoutes from '@/modules/employer/routes';
import followUpRoutes from '@/modules/follow-up/routes';
import importedEmploymentRoutes from '@/modules/imported-employments/routes';
import integrationsRoutes from '@/modules/integrations/routes';
import performanceRoutes from '@/modules/performances/routes';
import { defineAsyncComponent } from 'vue';
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';

// GUARDS
import { ModuleKey, RoleKey } from '@nxtpeople/nxt-vue-library';
import { registerGuards } from './guards/registerGuards';

export const privateRoutes: Array<RouteRecordRaw> = [
   {
      path: '/candidates',
      name: 'Candidates',
      component: () => import('@nxtpeople/nxt-styles').then((m) => m.NxtCandidates),
      meta: { permission: 'employees', titleKey: 'nxt-candidates.candidate', searchable: true },
      redirect: { name: 'CandidatesOverview' },
      children: [
         {
            path: '',
            name: 'CandidatesOverview',
            component: () => import('@nxtpeople/nxt-styles').then((m) => m.NxtCandidatesOverviewPage),
         },
         {
            path: 'create',
            name: 'CreateCandidate',
            redirect: '/candidates#create-candidate',
            meta: { titleKey: 'actions.add', searchable: true },
         },
      ],
   },
   {
      path: '/follow-up',
      name: 'FollowUp',
      component: () => import('@/modules/follow-up/pages/Index.vue'),
      meta: { titleKey: 'nxt-follow-up.follow-up', searchable: true },
      redirect: { name: 'FollowUpOverview' },
      children: followUpRoutes,
   },
   {
      path: '',
      alias: '/contracts',
      name: 'Contracts',
      meta: { permission: 'contracts', titleKey: 'contracts.contract', searchable: true },
      component: defineAsyncComponent(() => import('@/modules/contract/pages/Index.vue')),
      children: contractRoutes,
   },
   {
      path: '/customers',
      name: 'Customers',
      meta: { permission: 'customers', titleKey: 'customers.customer', searchable: true },
      component: () => import('@/modules/customer/pages/Index.vue'),
      children: customerRoutes,
      redirect: { name: 'CustomersOverview' },
   },
   {
      path: '/customer-groups',
      name: 'CustomerGroups',
      meta: { permission: 'customers', titleKey: 'customer-groups.customer-group', searchable: true },
      component: () => import('@/modules/customer-groups/pages/Index.vue'),
      children: customerGroupRoutes,
      redirect: { name: 'CustomerGroupsOverview' },
   },
   {
      path: '/employers',
      name: 'Employers',
      meta: { permission: 'employers', scopes: ['view', 'update'], titleKey: 'employers.employer', searchable: true },
      component: () => import('@/modules/employer/Index.vue'),
      children: employerRoutes,
      redirect: { name: 'EmployersOverview' },
   },
   {
      path: '/performances',
      name: 'Performances',
      meta: { permission: 'performances', titleKey: 'performances.performance', searchable: true },
      component: () => import('@/modules/performances/Index.vue'),
      children: performanceRoutes,
   },
   {
      path: '/integrations',
      name: 'Integrations',
      component: () => import('@/modules/integrations/pages/Index.vue'),
      meta: { titleKey: 'integrations.integration', searchable: true },
      children: integrationsRoutes,
      redirect: { name: 'Dimona' },
   },
   {
      path: '/imported-employments',
      name: 'ImportedEmployments',
      meta: { permission: 'contracts', scopes: ['create', 'view'], titleKey: 'imported-employments.imported-employment', searchable: true },
      component: () => import('@/modules/imported-employments/Index.vue'),
      children: importedEmploymentRoutes,
   },
   {
      path: '/reporting',
      name: 'Reporting',
      component: () => import('@/modules/reporting/pages/Index.vue'),
      meta: { titleKey: 'reporting.reporting', roles: [{ moduleKey: ModuleKey.NxtPay, roleKey: RoleKey.TenantAdmin }], searchable: true },
   },
   {
      path: '/documents',
      name: 'Documents',
      component: () => import('@/modules/document/pages/Index.vue'),
      meta: { titleKey: 'documents.document' },
   },
   {
      path: '/bug-report',
      name: 'BugReportPage',
      component: () => import('@/core/pages/BugReportPage.vue'),
      meta: { titleKey: 'generic.feedback.report-bug' },
   },
];

export const publicRoutes: Array<RouteRecordRaw> = [
   {
      path: '/upload-case-file-document/:caseFileStepSyncId',
      name: 'UploadCaseFileDocument',
      component: () => import('@/public-modules/case-file-documents/pages/UploadCaseFileDocument.vue'),
      meta: { titleKey: 'case-files.upload-document' },
   },
   {
      path: '/imw/sign/:token',
      name: 'ImwSign',
      component: () => import('@/public-modules/imw/pages/ImwSignPage.vue'),
      meta: { titleKey: 'imw.sign.title' },
   },
   {
      path: '/not-found',
      name: 'NotFound',
      component: () => import('@/core/pages/NotFoundPage.vue'),
      meta: { titleKey: 'errors.page-not-found' },
   },
   {
      path: '/error',
      name: 'ErrorPage',
      component: () => import('@/core/pages/ErrorPage.vue'),
      meta: { titleKey: 'errors.error' },
   },
   {
      path: '/no-permissions',
      name: 'NoPermissions',
      component: () => import('@/core/pages/NoPermissionsPage.vue'),
      meta: { titleKey: 'errors.no-permissions' },
   },
   {
      path: '/unavailable',
      name: 'Unavailable',
      component: () => import('@/core/pages/Unavailable.vue'),
      meta: { titleKey: 'generic.unavailable.title' },
   },
];

export const routes: Array<RouteRecordRaw> = [
   {
      path: '/',
      name: 'AppPrivate',
      component: () => import('@/AppPrivate.vue'),
      children: privateRoutes,
   },
   ...publicRoutes,
   {
      path: '/:pathMatch(.*)*',
      redirect: '/not-found',
   },
];

const router = createRouter({
   history: createWebHistory(),
   routes,
});

// Register all guards in a single place for clarity
registerGuards(router, privateRoutes);

export default router;
